import React, { useState } from 'react'
import styled from 'styled-components'
import { ApolloProvider } from 'react-apollo'
import { client } from './apollo/client'
import { Route, Switch, BrowserRouter, Redirect } from 'react-router-dom'
import GlobalPage from './pages/GlobalPage'
import TokenPage from './pages/TokenPage'
import PairPage from './pages/PairPage'
import { useGlobalData, useGlobalChartData, useEnergiswapRouterImplAddress } from './contexts/GlobalData'
import { isAddress } from './utils'
import AccountPage from './pages/AccountPage'
import AllTokensPage from './pages/AllTokensPage'
import AllPairsPage from './pages/AllPairsPage'
import PinnedData from './components/PinnedData'
// Referral program removed
// import AffiliateAnalytics from './pages/AffiliateAnalytics'
// import AffiliatePage from './pages/AffiliatePage'
import SideNav from './components/SideNav'
import AccountLookup from './pages/AccountLookup'
import { TOKEN_BLACKLIST, PAIR_BLACKLIST } from './constants'
import LocalLoader from './components/LocalLoader'
import Header from './components/Header'
import { Menu, X } from 'react-feather'
import { useMedia } from 'react-use'

const AppWrapper = styled.div`
  position: relative;
  width: 100%;
`
const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: ${({ open }) => (open ? '220px 1fr 200px' : '220px 1fr')};

  @media screen and (max-width: 1080px) {
    grid-template-columns: ${({ open }) => (open ? '72px 1fr 200px' : '72px 1fr')};
  }

  @media screen and (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`

const Right = styled.div`
  position: fixed;
  right: 0;
  bottom: 0rem;
  z-index: 99;
  width: ${({ open }) => (open ?? '220px')};
  height: fit-content;
  overflow: auto;
  background-color: ${({ theme }) => theme.bg1};
  @media screen and (max-width: 800px) {
    display: none;
  }
`

const Center = styled.div`
  height: 100%;
  z-index: 9999;
  transition: width 0.25s ease;
  background-color: ${({ theme }) => theme.onlyLight};
`

const BlurScreen = styled.div`
  height: calc(100% - 72px);
  position: absolute;
  z-index: 99999;
  width: calc(100% - 220px);
  left: 220px;
  transition: width 0.25s ease;
  background-color: ${({ theme }) => theme.black};
  opacity: 0.75;
`


const XIcon = styled(X)`
  color: theme.white;
  :hover {
    cursor: pointer;
    stroke-width: 3.5;
  }
`

const MenuIcon = styled(Menu)`
  color: theme.white;
  :hover {
    cursor: pointer;
    stroke-width: 3;
  }
`

const HeaderWrapper = styled.div`
  background-color: ${({ theme }) => theme.black};
  color: ${({ theme }) => theme.text1};
  padding: 0px 10px 0px 18px;
  position: sticky;
  top: 0px;
  z-index: 99999;
  box-sizing: border-box;

  @media screen and (max-width: 1080px) {
    display: grid;
    grid-template-columns: 50px 1fr;
  }

  @media screen and (max-width: 600px) {
    padding: 0.8rem;
  }

  @media screen and (max-width: 400px) {
    padding: 0.6rem;
  }
`

const MenuButton = styled.div`
  display: flex;
  align-items: center;
  padding-left: 8px;
`

/**
 * Wrap the component with the header and sidebar pinned tab
 */
const LayoutWrapper = ({ children, properties }) => {
  const { savedOpen, setSavedOpen, sideMenuOpen, setSideMenuOpen } = properties
  const below800 = useMedia('(max-width: 800px)')
  const below1080 = useMedia('(max-width: 1080px)')

  return (
    <>
      <HeaderWrapper>
        {below1080 && !below800 && <MenuButton>
          {sideMenuOpen
            ? <XIcon color={'white'} onClick={() => setSideMenuOpen(false)} />
            : <MenuIcon color={'white'} onClick={() => setSideMenuOpen(true)} />
          }
        </MenuButton>}

        <Header open={savedOpen} setSavedOpen={setSavedOpen} />
      </HeaderWrapper>
      <ContentWrapper open={savedOpen} side={sideMenuOpen}>

        <SideNav sideMenuOpen={sideMenuOpen} setSideMenuOpen={setSideMenuOpen} />
        <Center id="center">{sideMenuOpen && <BlurScreen />}{children}</Center>
        <Right open={savedOpen}>
          <PinnedData open={savedOpen} setSavedOpen={setSavedOpen} />
        </Right>
      </ContentWrapper>
    </>
  )
}


function App() {
  const [savedOpen, setSavedOpen] = useState(false)
  const [sideMenuOpen, setSideMenuOpen] = useState(false)

  const globalData = useGlobalData()
  const globalChartData = useGlobalChartData()
  const energiswapRouterImplAddress = useEnergiswapRouterImplAddress()

  const properties = {
    savedOpen,
    setSavedOpen,
    sideMenuOpen,
    setSideMenuOpen,
  }

  return (
    <ApolloProvider client={client}>
      <AppWrapper>
        {(globalData && energiswapRouterImplAddress) &&
          Object.keys(globalData).length > 0 &&
          globalChartData &&
          Object.keys(globalChartData).length > 0 ? (
          <BrowserRouter>
            <Switch>
              <Route
                exacts
                strict
                path="/token/:tokenAddress"
                render={({ match }) => {
                  if (!!TOKEN_BLACKLIST[match.params.tokenAddress.toLowerCase()]) {
                    return <Redirect to="/" />
                  }
                  if (isAddress(match.params.tokenAddress.toLowerCase())) {
                    return (
                      <LayoutWrapper properties={properties} >
                        <TokenPage address={match.params.tokenAddress.toLowerCase()} />
                      </LayoutWrapper>
                    )
                  } else {
                    return <Redirect to="/" />
                  }
                }}
              />
              <Route
                exacts
                strict
                path="/pair/:pairAddress"
                render={({ match }) => {
                  if (PAIR_BLACKLIST.includes(match.params.pairAddress.toLowerCase())) {
                    return <Redirect to="/" />
                  }
                  if (isAddress(match.params.pairAddress.toLowerCase())) {
                    return (
                      <LayoutWrapper properties={properties}>
                        <PairPage pairAddress={match.params.pairAddress.toLowerCase()} />
                      </LayoutWrapper>
                    )
                  } else {
                    return <Redirect to="/" />
                  }
                }}
              />
              <Route
                exacts
                strict
                path="/account/:accountAddress"
                render={({ match }) => {
                  if (isAddress(match.params.accountAddress.toLowerCase())) {
                    return (
                      <LayoutWrapper properties={properties}>
                        <AccountPage account={match.params.accountAddress.toLowerCase()} />
                      </LayoutWrapper>
                    )
                  } else {
                    return <Redirect to="/" />
                  }
                }}
              />
              {/* Referral program removed
              <Route
                exacts
                strict
                path="/affiliate-analytics/:affiliateAddress"
                render={({ match }) => {
                  if (isAddress(match.params.affiliateAddress.toLowerCase())) {
                    return (
                      <LayoutWrapper properties={properties}>
                        <AffiliatePage affiliate={match.params.affiliateAddress.toLowerCase()} />
                      </LayoutWrapper>
                    )
                  } else {
                    return <Redirect to="/" />
                  }
                }}
              /> */}

              <Route path="/tokens">
                <LayoutWrapper properties={properties}>
                  <AllTokensPage />
                </LayoutWrapper>
              </Route>

              <Route path="/pairs">
                <LayoutWrapper properties={properties}>
                  <AllPairsPage />
                </LayoutWrapper>
              </Route>

              <Route path="/accounts">
                <LayoutWrapper properties={properties}>
                  <AccountLookup />
                </LayoutWrapper>
              </Route>

              {/* Referral program removed
              <Route path="/affiliate-analytics">
                <LayoutWrapper properties={properties}>
                  <AffiliateAnalytics />
                </LayoutWrapper>
              </Route> */}

              <Route path="/">
                <LayoutWrapper properties={properties}>
                  <GlobalPage />
                </LayoutWrapper>
              </Route>
            </Switch>
          </BrowserRouter>
        ) : (
          <LocalLoader fill="true" />
        )}
      </AppWrapper>
    </ApolloProvider >
  )
}

export default App
